import { useCallback, useRef } from "react";
import { stableStringify } from "@/lib/utils/stable-stringify";

/**
 * One idempotency key per LOGICAL request — the `clientRequestId` a send
 * dialog puts on its `submitBackgroundJob` payload.
 *
 * `current(payload)` mints the key lazily FOR THAT PAYLOAD and returns the
 * same value while the payload stays the same, until `consume()` retires it.
 * A FAILED attempt (ServerError, thrown) does NOT consume: the retry of the
 * same payload replays the same key and the backend dedupes, so a submit
 * whose response was lost cannot become a second job. A SUCCESSFUL submit
 * consumes, so a still-mounted dialog's next send mints a fresh key and is a
 * new job.
 *
 * The key is BOUND to the payload because the backend answers a known key
 * with the job it already has, whatever the new body says. A key that
 * outlived an edit (recipients, "mark as contacted", sender name, template)
 * would therefore answer the edited send with the ORIGINAL job: the dialog
 * closes green and the operator's second choice never existed. So an edit
 * after a failed send is a new request with a new key — derived from the
 * payload itself, not from a hand-kept list of "fields that matter".
 *
 * Payload identity is structural (`stableStringify`, the same helper the
 * builders' dirty checks use): key order is irrelevant, and `undefined`
 * fields equal absent ones, exactly as the wire (`JSON.stringify`) sees them.
 *
 * Shape `^[A-Za-z0-9._:-]{1,64}$` on both paths (UUID, or a base-36
 * timestamp + random fallback where `crypto.randomUUID` is missing).
 */
export function useClientRequestId(): {
  current: (payload: unknown) => string;
  consume: () => void;
} {
  const ref = useRef<{ key: string; signature: string } | null>(null);

  const current = useCallback((payload: unknown): string => {
    const signature = stableStringify(payload);
    if (ref.current === null || ref.current.signature !== signature) {
      ref.current = { key: mintClientRequestId(), signature };
    }
    return ref.current.key;
  }, []);

  const consume = useCallback((): void => {
    ref.current = null;
  }, []);

  return { current, consume };
}

function mintClientRequestId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 12);
}
